import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { CarrierWarehouse } from '@magimp/shared';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  ToolbarActions,
} from '../../../ui/index.js';
import { listWarehouses } from '../../../api/requests/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';

export function WarehousesPage() {
  const { t } = useTranslation(['warehouses', 'common']);
  const [q, setQ] = useState('');
  const showToast = useToastStore((s) => s.show);
  const query = useQuery({
    queryKey: ['warehouses', q],
    queryFn: () => listWarehouses(q.trim() ? { q: q.trim() } : {}),
  });

  async function copyDescription(row: CarrierWarehouse): Promise<void> {
    try {
      await navigator.clipboard.writeText(row.description);
      showToast({ tone: 'success', text: t('warehouses:toasts.copied') });
    } catch {
      showToast({ tone: 'error', text: t('warehouses:toasts.copyFailed') });
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>
          {t('warehouses:title')}
        </h1>
        <ToolbarActions>
          <Input value={q} onChange={setQ} icon="search" placeholder={t('warehouses:search')} />
          <Button icon="refresh-cw" kind="ghost" onClick={() => void query.refetch()}>
            {t('common:actions.reload')}
          </Button>
        </ToolbarActions>
      </header>
      {query.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      )}
      {query.isPending && <LoadingState />}
      {query.isSuccess && (
        <DataTable
          rows={query.data.items}
          onSelect={(row) => {
            void copyDescription(row);
          }}
          emptyState={<EmptyState title={t('common:states.emptyTitle')} text={t('empty')} />}
          columns={[
            { key: 'carrierName', label: t('columns.carrier'), sortable: true },
            { key: 'city', label: t('columns.city'), sortable: true, width: 160 },
            { key: 'description', label: t('columns.description') },
          ]}
        />
      )}
    </div>
  );
}
